// Full-screen image lightbox. Portal'd to document.body (or to the modal
// <dialog> it was opened from) so it escapes any transformed/scrolled
// ancestor. Dismisses on:
//   - tap on the dark backdrop (anywhere outside the image)
//   - tap the X close button
//   - Escape key
//
// Download sits beside the close button, top right. It is a plain link with a
// `download` name, so an object URL saves from memory with no new request.
//
// When the clicked image belongs to a message with several images, the whole
// set opens as a GALLERY: ‹ › buttons and the ←/→ arrow keys page through it
// (wrapping), with an "n / total" counter. A lone image shows no nav chrome.
//
// Used by UserBubble (sent-image thumbnails) + AssistantBubble (agent images) +
// Composer pending-attachment previews. State lives in the session store as
// `lightboxItem` / `lightboxItems` / `lightboxIndex`.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from "react-feather";
import { downloadableHref, imageDownloadName } from "../lib/imageDownload";
import "./LightboxZoom.css";

interface Props {
  src: string;
  filename?: string;
  /** The image's MIME type: gives the saved file an extension when `filename` has none. */
  mimeType?: string;
  onClose: () => void;
  /** Page the gallery by delta (wraps). Omit for a single image. */
  onStep?: (delta: number) => void;
  index?: number;
  total?: number;
  /** Opt-in for locally owned public-timeline previews; existing gallery behavior is retained. */
  manageFocus?: boolean;
  /** Where the preview mounts (document.body by default). Opened from inside a modal
   *  <dialog> it must be that dialog: everything outside the top layer paints under it
   *  and is inert. That dialog already locks the page scroll, so body is left alone. */
  container?: Element | null;
}

export default function Lightbox({ src, filename, mimeType, onClose, onStep, index = 0, total = 1, manageFocus = false, container }: Props) {
  const hasGallery = total > 1 && !!onStep;
  const dialog = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gestureMoved = useRef(false);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const currentView = useRef(view);
  function updateView(next: typeof view) {
    const scale = Math.max(1, Math.min(4, next.scale));
    const maxX = (image.current?.clientWidth ?? 0) * (scale - 1) / 2;
    const maxY = (image.current?.clientHeight ?? 0) * (scale - 1) / 2;
    const value = { scale, x: Math.max(-maxX, Math.min(maxX, next.x)), y: Math.max(-maxY, Math.min(maxY, next.y)) };
    currentView.current = value; setView(value);
  }
  useEffect(() => {
    pointers.current.clear(); gestureMoved.current = false;
    currentView.current = { scale: 1, x: 0, y: 0 }; setView(currentView.current);
  }, [src, index]);
  function pointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!pointers.current.size) gestureMoved.current = false;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size > 1 || currentView.current.scale > 1) {
      for (const id of pointers.current.keys()) event.currentTarget.setPointerCapture(id);
    }
  }
  function pointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    const before = [...pointers.current.values()];
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const after = [...pointers.current.values()], state = currentView.current;
    const dx = event.clientX - previous.x, dy = event.clientY - previous.y;
    if (Math.abs(dx) + Math.abs(dy) > 2) gestureMoved.current = true;
    if (after.length === 2) {
      gestureMoved.current = true;
      const distance = (points: typeof after) => Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      const oldDistance = distance(before);
      if (oldDistance < 1) return;
      const scale = Math.max(1, Math.min(4, state.scale * distance(after) / oldDistance));
      const bounds = event.currentTarget.getBoundingClientRect();
      const centre = (points: typeof after) => ({ x: (points[0].x + points[1].x) / 2 - bounds.left - bounds.width / 2,
        y: (points[0].y + points[1].y) / 2 - bounds.top - bounds.height / 2 });
      const old = centre(before), next = centre(after), ratio = scale / state.scale;
      updateView({ scale, x: next.x + (state.x - old.x) * ratio, y: next.y + (state.y - old.y) * ratio });
    } else if (after.length === 1 && state.scale > 1) updateView({ ...state, x: state.x + dx, y: state.y + dy });
    if (event.cancelable) event.preventDefault();
  }
  function pointerEnd(event: React.PointerEvent<HTMLDivElement>) { pointers.current.delete(event.pointerId); }
  const host = container ?? document.body;
  const nested = host !== document.body;
  useEffect(() => {
    if (!manageFocus) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    if (!nested) document.body.style.overflow = "hidden";
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
    function trap(event:KeyboardEvent) {
      if (event.key !== "Tab") return;
      const buttons = [...(dialog.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])") ?? [])];
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.current?.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.current?.contains(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    }
    // Capture: a modal dialog around the preview stops keys from bubbling to document.
    document.addEventListener("keydown",trap,true);
    return () => {
      document.removeEventListener("keydown",trap,true);
      if (!nested) document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus({preventScroll:true});
    };
  },[manageFocus,nested]);

  // Esc closes; ←/→ page the gallery.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // The preview owns this Escape: the dialog under it must not close too.
        if (manageFocus) e.preventDefault();
        onClose();
        return;
      }
      if (!hasGallery || !onStep) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onStep(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        onStep(1);
      }
    }
    document.addEventListener("keydown", onKey, manageFocus);
    return () => document.removeEventListener("keydown", onKey, manageFocus);
  }, [onClose, onStep, hasGallery, manageFocus]);

  // Clicks on the chrome must not bubble to the backdrop (which closes).
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const downloadHref = downloadableHref(src, window.location.href);
  const saveAs = imageDownloadName(filename, mimeType);

  return createPortal(
    <div
      ref={dialog}
      className="lightbox"
      onPointerDown={event => { if (event.target === event.currentTarget) { pointers.current.clear(); gestureMoved.current = false; } }}
      onClick={event => { if (event.target === event.currentTarget && !gestureMoved.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={filename ? `Image: ${filename}` : "Image preview"}
    >
      <div className="lightbox__actions">
        {downloadHref && (
          <a
            className="lightbox__download"
            href={downloadHref}
            download={saveAs}
            onClick={stop}
            aria-label="Download"
            title={`Download ${saveAs}`}
          >
            <Download width={18} height={18} aria-hidden="true" />
          </a>
        )}
        <button
          type="button"
          className="lightbox__close"
          onClick={(e) => {
            stop(e);
            onClose();
          }}
          aria-label="Close"
        >
          <X width={18} height={18} />
        </button>
      </div>

      {hasGallery && (
        <button
          type="button"
          className="lightbox__nav lightbox__nav--prev"
          onClick={(e) => {
            stop(e);
            onStep!(-1);
          }}
          aria-label="Previous image"
          title="Previous (←)"
        >
          <ChevronLeft width={24} height={24} />
        </button>
      )}

      <div className="lightbox__stage" onPointerDown={pointerDown} onPointerMove={pointerMove}
        onPointerUp={pointerEnd} onPointerCancel={pointerEnd}
        onLostPointerCapture={event => { if (event.target === event.currentTarget) pointerEnd(event); }}
        onClick={event => { stop(event); if (event.target === event.currentTarget && !gestureMoved.current && view.scale === 1) onClose(); }}>
      <img
        ref={image}
        className="lightbox__img"
        src={src}
        alt={filename ?? ""}
        draggable={false}
        onClick={stop}
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, cursor: view.scale > 1 ? 'grab' : undefined }}
      />
      </div>

      <div className="lightbox__zoom" role="group" aria-label="Image zoom" onClick={stop}>
        <button type="button" aria-label="Zoom out" disabled={view.scale <= 1} onClick={() => updateView({ ...currentView.current, scale: currentView.current.scale - .5 })}><ZoomOut size={18} aria-hidden="true" /></button>
        <button type="button" className="lightbox__zoomReset" aria-label="Reset zoom" title="Reset zoom and position" onClick={() => updateView({ scale: 1, x: 0, y: 0 })}>{Math.round(view.scale * 100)}%</button>
        <button type="button" aria-label="Zoom in" disabled={view.scale >= 4} onClick={() => updateView({ ...currentView.current, scale: currentView.current.scale + .5 })}><ZoomIn size={18} aria-hidden="true" /></button>
      </div>

      {hasGallery && (
        <button
          type="button"
          className="lightbox__nav lightbox__nav--next"
          onClick={(e) => {
            stop(e);
            onStep!(1);
          }}
          aria-label="Next image"
          title="Next (→)"
        >
          <ChevronRight width={24} height={24} />
        </button>
      )}

      {(filename || hasGallery) && (
        <div className="lightbox__caption" onClick={stop}>
          {filename}
          {hasGallery && (
            <span className="lightbox__counter">
              {index + 1} / {total}
            </span>
          )}
        </div>
      )}
    </div>,
    host,
  );
}
